import React, { useEffect } from "react"
import { Box } from "@mui/material"
import Typography from "@mui/material/Typography"
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined"
import RadioButtonCheckedOutlinedIcon from "@mui/icons-material/RadioButtonCheckedOutlined"
import PilotStationIcon from "@mui/icons-material/LocalParkingRounded"
import DoNotDisturbOffIcon from "@mui/icons-material/DoNotDisturbOff"
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn"
import ErrorIcon from "@mui/icons-material/Error"
import InfoIcon from "@mui/icons-material/Info"
import CircleIcon from "@mui/icons-material/CircleTwoTone"
import CustomCheckbox from "@/components/ui/CustomCheckbox"
import { ROW_LABEL_SX, ROW_DESCRIPTION_SX } from "@/theme/panelStyles"

const rowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: 0.75,
  py: 0.5,
  borderRadius: "8px",
  "&:hover": { bgcolor: "rgba(0, 79, 125, 0.08)" },
}

const labelSx = { ...ROW_LABEL_SX, whiteSpace: "nowrap" }

function LayerLabel({ title, desc }) {
  return (
    <Box>
      <Typography sx={labelSx}>{title}</Typography>
      {desc && <Typography sx={ROW_DESCRIPTION_SX}>{desc}</Typography>}
    </Box>
  )
}

function LegendSwatch({ color, fill }) {
  return (
    <Box
      sx={{
        ml: 1,
        width: 16,
        height: 12,
        border: `2px solid ${color}`,
        backgroundColor: fill,
      }}
    />
  )
}

export default function ToggleButtonsGeoVectorLayers({ toogleButtonsOnOff, setToogleButtonsOnOff }) {
  const nestedLegOptions = ["Usage", "Navigability", "Usability"]

  const handleChange = (event) => {
    const value = event.target.name
    const isNested = nestedLegOptions.includes(value)

    if (isNested) {
      if (event.target.checked) {
        setToogleButtonsOnOff((prev) => {
          // Clear other nested selections
          let newState = prev.filter((item) => !nestedLegOptions.includes(item))
          // Always keep Legs checked
          if (!newState.includes("Legs")) newState.push("Legs")
          // Add the newly selected nested option
          newState.push(value)
          return newState
        })
      } else {
        setToogleButtonsOnOff((prev) => {
          // Remove the unchecked nested
          let newState = prev.filter((item) => item !== value)
          // If Usage was unchecked, also remove Legs
          if (value === "Usage") {
            newState = newState.filter((item) => item !== "Legs")
          }
          return newState
        })
      }
      return
    } else if (value === "Legs") {
      if (event.target.checked) {
        setToogleButtonsOnOff((prev) => [...prev, "Legs"])
      } else {
        // Unchecking Legs clears all nested options
        setToogleButtonsOnOff((prev) => prev.filter((item) => item !== "Legs" && !nestedLegOptions.includes(item)))
      }
    } else {
      // Default for all other top-level checkboxes
      if (event.target.checked) {
        setToogleButtonsOnOff((prev) => [...prev, value])
      } else {
        setToogleButtonsOnOff((prev) => prev.filter((item) => item !== value))
      }
    }
  }

  // When Legs is turned on with no nested option, default to Usage
  useEffect(() => {
    if (toogleButtonsOnOff.includes("Legs")) {
      const hasNested = toogleButtonsOnOff.some((item) => nestedLegOptions.includes(item))
      if (!hasNested) {
        setToogleButtonsOnOff((prev) => [...prev, "Usage"])
      }
    }
  }, [toogleButtonsOnOff, setToogleButtonsOnOff])

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* ECA Zones */}
      <Box sx={rowSx}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CustomCheckbox
            checked={toogleButtonsOnOff.includes("ECA")}
            onChange={handleChange}
            name="ECA"
            aria-label="eca zones"
          />
          <LayerLabel title="ECA Zones" desc="Emission control areas" />
        </Box>
        <LegendSwatch color="green" fill="rgba(0, 255, 0, 0.08)" />
      </Box>

      {/* HighRisk/Piracy Zones */}
      <Box sx={rowSx}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CustomCheckbox
            checked={toogleButtonsOnOff.includes("HighRisk")}
            onChange={handleChange}
            name="HighRisk"
            aria-label="high risk piracy zones"
            color="default"
          />
          <LayerLabel title="HighRisk/Piracy Zones" desc="Piracy & armed-robbery zones" />
        </Box>
        <LegendSwatch color="red" fill="rgba(255, 0, 0, 0.1)" />
      </Box>

      {/* JWC Listed Areas (war risk) */}
      <Box sx={rowSx}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CustomCheckbox
            checked={toogleButtonsOnOff.includes("JWLA")}
            onChange={handleChange}
            name="JWLA"
            aria-label="war risk zones"
            color="default"
          />
          <LayerLabel title="War Risk Zones" desc="JWC listed areas (war-risk insurance)" />
        </Box>
        <LegendSwatch color="#7b1e3c" fill="rgba(123, 30, 60, 0.12)" />
      </Box>

      {/* Operational risk areas (ice, freak waves, gap winds, fog, munitions, fishing) */}
      <Box sx={rowSx}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CustomCheckbox
            checked={toogleButtonsOnOff.includes("OperationalRisk")}
            onChange={handleChange}
            name="OperationalRisk"
            aria-label="operational risk zones"
            color="default"
          />
          <LayerLabel title="Operational Risk Zones" desc="Ice, freak waves, gap winds, fog, munitions, fishing" />
        </Box>
        <LegendSwatch color="#0277bd" fill="rgba(2, 119, 189, 0.12)" />
      </Box>

      {/* Ports */}
      <Box sx={rowSx}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CustomCheckbox
            checked={toogleButtonsOnOff.includes("Ports")}
            onChange={handleChange}
            name="Ports"
            aria-label="ports"
          />
          <LayerLabel title="Ports" desc="Port locations" />
        </Box>
        <CircleIcon sx={{ fontSize: 18, color: "black" }} />
      </Box>

      {/* Legs nested section */}
      {(toogleButtonsOnOff.includes("Legs") || nestedLegOptions.some((opt) => toogleButtonsOnOff.includes(opt))) && (
        <Box sx={{ display: "flex", flexDirection: "column", pl: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CustomCheckbox
              icon={<RadioButtonUncheckedOutlinedIcon />}
              checkedIcon={<RadioButtonCheckedOutlinedIcon />}
              checked={toogleButtonsOnOff.includes("Usage")}
              onChange={handleChange}
              name="Usage"
              aria-label="Usage"
            />
            <Typography sx={labelSx}>Usage</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CustomCheckbox
              icon={<RadioButtonUncheckedOutlinedIcon />}
              checkedIcon={<RadioButtonCheckedOutlinedIcon />}
              checked={toogleButtonsOnOff.includes("Navigability")}
              onChange={handleChange}
              name="Navigability"
              aria-label="Navigability"
            />
            <Typography sx={labelSx}>Navigability</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CustomCheckbox
              icon={<RadioButtonUncheckedOutlinedIcon />}
              checkedIcon={<RadioButtonCheckedOutlinedIcon />}
              checked={toogleButtonsOnOff.includes("Usability")}
              onChange={handleChange}
              name="Usability"
              aria-label="Usability"
            />
            <Typography sx={labelSx}>Usability</Typography>
          </Box>
        </Box>
      )}

      {/* Regions */}
      <Box sx={rowSx}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CustomCheckbox
            checked={toogleButtonsOnOff.includes("Regions")}
            onChange={handleChange}
            name="Regions"
            aria-label="regions"
          />
          <LayerLabel title="Regions" desc="RNW regions" />
        </Box>
        <InfoIcon style={{ color: "blue", fontSize: 18 }} />
      </Box>

      {/* Restrictions */}
      <Box sx={rowSx}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CustomCheckbox
            checked={toogleButtonsOnOff.includes("Restrictions")}
            onChange={handleChange}
            name="Restrictions"
            aria-label="restrictions"
          />
          <LayerLabel title="Restrictions" desc="RNW safety restrictions" />
        </Box>
        <ErrorIcon style={{ color: "orange", fontSize: 18 }} />
      </Box>

      {/* Via Areas */}
      <Box sx={rowSx}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CustomCheckbox
            checked={toogleButtonsOnOff.includes("ViaAreas")}
            onChange={handleChange}
            name="ViaAreas"
            aria-label="via areas"
          />
          <LayerLabel title="Via Areas" desc="Open & closed via areas" />
        </Box>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <DoNotDisturbOnIcon style={{ color: "red", fontSize: 18 }} />
          <DoNotDisturbOffIcon style={{ color: "green", fontSize: 18 }} />
        </span>
      </Box>

      {/* Pilot Stations */}
      <Box sx={rowSx}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CustomCheckbox
            checked={toogleButtonsOnOff.includes("PilotStations")}
            onChange={handleChange}
            name="PilotStations"
            aria-label="pilot stations"
          />
          <LayerLabel title="Pilot Stations" desc="Pilot boarding points" />
        </Box>
        <div
          style={{
            width: "18px",
            height: "18px",
            borderRadius: "50%",
            background: "rgba(128, 0, 128, 0.12)",
            border: "2px solid rgba(128, 0, 128, 0.55)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backdropFilter: "saturate(110%)",
          }}
        >
          <PilotStationIcon style={{ color: "purple", fontSize: 16 }} />
        </div>
      </Box>
    </div>
  )
}
